import Link from 'next/link';
import { Box } from 'theme-ui';
import { useEffect, useRef, useState } from 'react';
import styles from '@features/cms/components/collections/ProductPremiumBlock/components/ProductPremiumProductDescription/ProductPremiumProductDescription.styles';
import ProductPremiumImage from '@features/cms/components/collections/ProductPremiumBlock/components/ProductPremiumImage/ProductPremiumImage';
import { IInspirationObject, IInteriorDesignObject, IProductPremiumProductDescriptionVariantBlock, IProductPremiumSingleBlock, TProductPremiumSingleBlockWitParsedImage } from '@features/cms/typescript';
import useProductPremiumSlide from '@hooks/useProductPremiumSlide';
import Heading from '@components/Heading/Heading';
import sanitizeHtml from '@utils/sanitizeHtml';
import { IParsedImage } from '@components/typescript';
import Button from '@components/Button/Button';
import getProductUrl from '@utils/getProductUrl';
import ProductState from '@features/categories/mobx/state/ProductState';
import useResponsive from '@features/responsive/hooks/useResponsive';
import stackedStyles from '@features/cms/components/collections/ProductPremiumBlock/ProductPremiumStacked.styles';
type PropsType = {
  blocks: TProductPremiumSingleBlockWitParsedImage[];
  activeBlock: number;
  viewportHeight: number;
  viewportWidth: number;
  isFirst: boolean;
  isStackedMobile?: boolean;
};
const ProductPremiumProductDescription = ({
  blocks,
  activeBlock,
  viewportHeight,
  viewportWidth,
  isFirst,
  isStackedMobile = false
}: PropsType) => {
  const slideContainerRef = useRef<HTMLDivElement>(null);
  const mainImageConatinerRef = useRef<HTMLDivElement>(null);
  const productImageContainerRef = useRef<HTMLDivElement>(null);
  const [desktopContentHeight, setDesktopContentHeight] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLElement>(null);
  const [imageHeight, setImageHeight] = useState(0);
  const {
    isMobile: isMobileSmall,
    isMobilelg,
    isTablet: isTabletSmall,
    isTabletlg
  } = useResponsive();
  const isMobile = isMobileSmall || isMobilelg;
  const isTablet = isTabletSmall || isTabletlg;
  const {
    previousBlock,
    currentBlock
  } = useProductPremiumSlide(blocks, activeBlock);
  const [blockData, setBlockData] = useState<IProductPremiumSingleBlock<IProductPremiumProductDescriptionVariantBlock, IParsedImage>>(currentBlock as IProductPremiumSingleBlock<IProductPremiumProductDescriptionVariantBlock, IParsedImage>);
  const block = currentBlock as IProductPremiumSingleBlock<IProductPremiumProductDescriptionVariantBlock, IParsedImage>;
  const {
    productDescriptionImage: productImage,
    productDescriptionVideo: productVideo,
    productDescriptionShowButton: showButton
  } = blockData.variant;
  const productObject = blockData.objectType === 'Product' && blockData.object ? new ProductState(blockData.object) : null;
  const inspirationObject: IInspirationObject | null = blockData.objectType === 'Inspiration' ? blockData.object as IInspirationObject : null;
  const interiorDesignObject: IInteriorDesignObject | null = blockData.objectType === 'InteriorDesign' ? blockData.object as IInteriorDesignObject : null;
  let objectLink: string | null = null;
  if (productObject) objectLink = getProductUrl(productObject.name, productObject.id);
  if (inspirationObject) objectLink = `/inspirations/ideas/${inspirationObject.slug}`;
  if (interiorDesignObject) objectLink = `/inspirations/interior-designs/${interiorDesignObject.slug}`;
  const shouldDisplayButton = showButton === '1';
  const recalculateImageHeight = () => {
    if (contentRef.current === null) return 0;
    const contentDivHeight = contentRef.current.clientHeight;
    const pageMaring = isMobile ? 24 : 160;
    const slideMargin = isMobile ? 0 : 64;
    const slideContentMargin = isMobile ? 16 : 40;
    const gapMargin = 4;
    let imageHeightCalc = isMobile ? viewportHeight - viewportWidth / 4 * 3 - contentDivHeight - pageMaring - slideMargin - slideContentMargin - gapMargin : (viewportHeight - contentDivHeight - pageMaring - slideMargin - slideContentMargin - gapMargin) / 2;
    const productImageContainerWidth = productImageContainerRef.current?.clientWidth || 0;
    const imageHeightFromAspectRatio = productImageContainerWidth / 4 * 3;
    if (imageHeightCalc > imageHeightFromAspectRatio && isMobile) imageHeightCalc = imageHeightFromAspectRatio;
    // Keep a usable fallback instead of -1px which collapses <picture> to height 0.
    if (imageHeightCalc < 120) {
      imageHeightCalc = imageHeightFromAspectRatio > 0 ? imageHeightFromAspectRatio : viewportWidth / 4 * 3;
    }
    return imageHeightCalc;
  };
  const applyContentWidthAndImageHeight = () => {
    if (contentRef.current === null) return;
    const imageCalcHeight = recalculateImageHeight();
    const contetnWidthCalc = isMobile ? viewportWidth / 4 * 3 : imageCalcHeight / 3 * 4;
    contentRef.current.style.width = isMobile || !isMobile && !isTablet ? '100%' : `${contetnWidthCalc}px`;
    setImageHeight(imageCalcHeight);
  };
  useEffect(() => {
    if (!isMobile && !isTablet) {
      setTimeout(() => {
        if (contentRef.current === null) return;
        const {
          clientHeight
        } = contentRef.current.firstElementChild as HTMLDivElement;
        setDesktopContentHeight(clientHeight);
      }, 0);
    }
    applyContentWidthAndImageHeight();
    const timeoutId = window.setTimeout(applyContentWidthAndImageHeight, 50);
    return () => window.clearTimeout(timeoutId);
  }, [blockData, viewportHeight, viewportWidth, isMobile, isTablet]);
  useEffect(() => {
    if (productImageContainerRef.current === null || contentRef.current === null || slideContainerRef.current === null || mainImageConatinerRef.current === null) return;
    if (isTablet) {
      if (activeBlock % 2 === 0) {
        mainImageConatinerRef.current.style.opacity = '0';
        setTimeout(() => {
          if (slideContainerRef.current === null || mainImageConatinerRef.current === null) return;
          slideContainerRef.current.style.top = '80px';
          mainImageConatinerRef.current.style.top = 'unset';
          mainImageConatinerRef.current.style.bottom = '80px';
          setTimeout(() => {
            if (slideContainerRef.current === null || mainImageConatinerRef.current === null) return;
            mainImageConatinerRef.current.style.opacity = '1';
            if (contentRef.current) contentRef.current.style.opacity = '1';
          }, 400);
        }, 300);
      } else {
        mainImageConatinerRef.current.style.opacity = '0';
        setTimeout(() => {
          if (slideContainerRef.current === null || mainImageConatinerRef.current === null) return;
          const height = slideContainerRef.current.clientHeight;
          slideContainerRef.current.style.top = `calc(100% - 80px - ${height}px)`;
          mainImageConatinerRef.current.style.top = '80px';
          mainImageConatinerRef.current.style.bottom = 'unset';
          setTimeout(() => {
            if (slideContainerRef.current === null || mainImageConatinerRef.current === null) return;
            mainImageConatinerRef.current.style.opacity = '1';
            if (contentRef.current) contentRef.current.style.opacity = '1';
          }, 400);
        }, 300);
      }
      setTimeout(() => {
        setBlockData({
          ...block
        });
        if (contentRef.current) contentRef.current.style.opacity = '1';
      }, 400);
    }
    if (isMobile) {
      contentRef.current.style.opacity = '0.5';
      setTimeout(() => {
        setBlockData({
          ...block
        });
        if (contentRef.current) contentRef.current.style.opacity = '1';
      }, 200);
    }
    if (!isMobile && !isTablet) {
      contentRef.current.style.opacity = '0.5';
      setTimeout(() => {
        if (productImageContainerRef.current === null || slideContainerRef.current === null || mainImageConatinerRef.current === null) return;
        const productImageWidth = slideContainerRef.current.clientWidth;
        const productImageHeightCalc = productImageWidth / 4 * 3;
        productImageContainerRef.current.style.height = `${productImageHeightCalc}px`;
        const mainImageWidth = mainImageConatinerRef.current.clientWidth;
        const mainImageHeightCalc = mainImageWidth / 4 * 3;
        mainImageConatinerRef.current.style.height = `${mainImageHeightCalc}px`;
        slideContainerRef.current.style.height = `${mainImageHeightCalc}px`;
      }, 50);
      setTimeout(() => {
        setBlockData({
          ...block
        });
        if (contentRef.current) contentRef.current.style.opacity = '1';
      }, 200);
    }
  }, [currentBlock, viewportHeight, viewportWidth, isMobile, isTablet]);
  useEffect(() => {
    if (productImageContainerRef.current === null || contentRef.current === null || slideContainerRef.current === null || mainImageConatinerRef.current === null) return;
    if (isMobile || isTablet) {
      setTimeout(() => {
        if (productImageContainerRef.current === null || contentRef.current === null || slideContainerRef.current === null || mainImageConatinerRef.current === null) return;
        mainImageConatinerRef.current.style.height = 'auto';
        productImageContainerRef.current.style.height = 'auto';
        slideContainerRef.current.style.height = 'auto';
      }, 400);
    }
    if (!isTablet) {
      setTimeout(() => {
        if (productImageContainerRef.current === null || slideContainerRef.current === null || mainImageConatinerRef.current === null) return;
        slideContainerRef.current.style.top = 'unset';
        slideContainerRef.current.style.bottom = 'unset';
        mainImageConatinerRef.current.style.top = 'unset';
        mainImageConatinerRef.current.style.bottom = 'unset';
      }, 400);
    }
  }, [isMobile, isTablet, isStackedMobile]);
  if (isStackedMobile) {
    const stackedBlock = blocks[0] as IProductPremiumSingleBlock<IProductPremiumProductDescriptionVariantBlock, IParsedImage>;
    const {
      productDescriptionShowButton: stackedShowButton
    } = stackedBlock.variant;
    const stackedProductObject = stackedBlock.objectType === 'Product' && stackedBlock.object ? new ProductState(stackedBlock.object) : null;
    const stackedInspirationObject: IInspirationObject | null = stackedBlock.objectType === 'Inspiration' ? stackedBlock.object as IInspirationObject : null;
    const stackedInteriorDesignObject: IInteriorDesignObject | null = stackedBlock.objectType === 'InteriorDesign' ? stackedBlock.object as IInteriorDesignObject : null;
    let stackedObjectLink: string | null = null;
    if (stackedProductObject) stackedObjectLink = getProductUrl(stackedProductObject.name, stackedProductObject.id);
    if (stackedInspirationObject) stackedObjectLink = `/inspirations/ideas/${stackedInspirationObject.slug}`;
    if (stackedInteriorDesignObject) stackedObjectLink = `/inspirations/interior-designs/${stackedInteriorDesignObject.slug}`;
    const stackedShouldDisplayButton = stackedShowButton === '1';
    return <div sx={stackedStyles.slide} data-testid="product-premium-product-description-slide">
        <div sx={stackedStyles.mainImageProduct} data-testid="product-premium-product-description-main-image">
          <ProductPremiumImage image={stackedBlock.image} video={stackedBlock.video} fullScreen />
        </div>
        <div sx={stackedStyles.contentProduct} data-testid="product-premium-product-description-content">
          <Heading h={isFirst ? 1 : 2} headingType={2} mobile>
            <span dangerouslySetInnerHTML={sanitizeHtml(stackedBlock.variant.productDescriptionTitle)} />
          </Heading>
          <p className="big" sx={styles.productDescriptionVariantText(stackedShouldDisplayButton)}>
            {stackedBlock.variant.productDescriptionDescription}
          </p>
          {stackedShouldDisplayButton && stackedObjectLink && <Link href={stackedObjectLink} aria-label={stackedProductObject?.name}>
              <Button variantColor="dark" size="small" letterCase="lowercase">
                {stackedBlock.variant.productDescriptionButtonText}
              </Button>
            </Link>}
        </div>
      </div>;
  }
  return <div sx={styles.sliderGrid}>
      <Box sx={styles.descriptionVariantContainer} ref={slideContainerRef}>
        <div sx={styles.descriptionVariantWrapper(previousBlock.position, currentBlock.position)}>
          <div sx={styles.productDescriptionImageContainer} ref={productImageContainerRef}>
            <ProductPremiumImage image={productImage} video={productVideo} height={imageHeight} ref={imageRef} alignLeft />
          </div>
          <div sx={styles.contentContainer(desktopContentHeight)} ref={contentRef}>
            <div>
              <Heading h={isFirst ? 1 : 2} headingType={2} mobile>
                <span dangerouslySetInnerHTML={sanitizeHtml(blockData.variant.productDescriptionTitle)} />
              </Heading>
              <p className="big" sx={styles.productDescriptionVariantText(shouldDisplayButton)}>
                {blockData.variant.productDescriptionDescription}
              </p>
              {shouldDisplayButton && objectLink && <Link href={objectLink} aria-label={productObject?.name}>
                  <Button variantColor="dark" size="small" letterCase="lowercase">
                    {blockData.variant.productDescriptionButtonText}
                  </Button>
                </Link>}
            </div>
          </div>
        </div>
      </Box>
      <div sx={styles.mainImageContainer} ref={mainImageConatinerRef}>
        <ProductPremiumImage image={currentBlock.image} video={currentBlock.video} height={imageHeight} fullScreen={!!isMobile} />
      </div>
    </div>;
};
export default ProductPremiumProductDescription;